// stores/productStore.js
import { create } from "zustand";
import axiosToken from "../api/axiosClient";

export const useProductStore = create((set, get) => ({
  // États pour les produits
  products: [],
  currentProduct: null,
  loading: false,
  error: null,

  // États pour les mouvements
  movements: [],
  currentMovement: null,
  movementsLoading: false,

  // États pour les rapports
  inventoryReport: [],
  statisticsReport: null,
  historyReport: [],

  // États pour les alertes
  alerts: [],
  alertsLoading: false,

  // =============================================
  // CRUD PRODUITS
  // =============================================

  // Récupérer tous les produits
  fetchProducts: async () => {
    set({ loading: true, error: null });
    try {
      const response = await axiosToken.get("/products");
      const productsData = response.data.data;

      // Adapter selon la structure de votre API
      let products = [];
      if (productsData.produits) {
        // Structure de l'endpoint de rapport
        products = productsData.produits.map((item) => ({
          ...item.product,
          stockActuel: item.stockActuel,
          statutAlerte: item.statutAlerte,
        }));
      } else if (productsData.products) {
        // Structure alternative
        products = productsData.products;
      } else {
        // Structure par défaut
        products = productsData;
      }

      set({
        products: products,
        loading: false,
      });
    } catch (error) {
      set({
        error:
          error.response?.data?.message ||
          "Erreur lors de la récupération des produits",
        loading: false,
      });
      throw error;
    }
  },

  // Récupérer un produit par ID
  fetchProductById: async (id) => {
    set({ loading: true, error: null });
    try {
      const response = await axiosToken.get(`/products/${id}`);
      set({
        currentProduct: response.data.data?.product || response.data.data,
        loading: false,
      });
      return response.data.data?.product || response.data.data;
    } catch (error) {
      set({
        error:
          error.response?.data?.message ||
          "Erreur lors de la récupération du produit",
        loading: false,
      });
      throw error;
    }
  },

  // Créer un produit
  createProduct: async (productData) => {
    set({ loading: true, error: null });
    try {
      const response = await axiosToken.post("/products", productData);
      const newProduct = response.data.data?.product || response.data.data;

      // Ajouter à la liste des produits
      set((state) => ({
        products: [...state.products, newProduct],
        loading: false,
      }));

      return newProduct;
    } catch (error) {
      set({
        error:
          error.response?.data?.message ||
          "Erreur lors de la création du produit",
        loading: false,
      });
      throw error;
    }
  },

  // Mettre à jour un produit
  updateProduct: async (id, productData) => {
    set({ loading: true, error: null });
    try {
      const response = await axiosToken.put(`/products/${id}`, productData);
      const updatedProduct = response.data.data?.product || response.data.data;

      // Mettre à jour dans la liste
      set((state) => ({
        products: state.products.map((p) =>
          p.id === parseInt(id) ? updatedProduct : p,
        ),
        currentProduct:
          state.currentProduct?.id === parseInt(id)
            ? updatedProduct
            : state.currentProduct,
        loading: false,
      }));

      return updatedProduct;
    } catch (error) {
      set({
        error:
          error.response?.data?.message ||
          "Erreur lors de la mise à jour du produit",
        loading: false,
      });
      throw error;
    }
  },

  // Supprimer un produit
  deleteProduct: async (id) => {
    set({ loading: true, error: null });
    try {
      await axiosToken.delete(`/products/${id}`);

      // Retirer de la liste
      set((state) => ({
        products: state.products.filter((p) => p.id !== parseInt(id)),
        currentProduct:
          state.currentProduct?.id === parseInt(id)
            ? null
            : state.currentProduct,
        loading: false,
      }));
    } catch (error) {
      set({
        error:
          error.response?.data?.message ||
          "Erreur lors de la suppression du produit",
        loading: false,
      });
      throw error;
    }
  },

  // =============================================
  // GESTION DES MOUVEMENTS DE STOCK
  // =============================================

  // Créer un mouvement de stock
  createMovement: async (movementData) => {
    set({ loading: true, error: null });
    try {
      const response = await axiosToken.post(
        "/products/movements",
        movementData,
      );
      const newMovement = response.data.data?.movement || response.data.data;

      // Mettre à jour le produit concerné si nécessaire
      const { productId, type, quantity } = movementData;
      if (
        productId &&
        (type === "ENTREE" ||
          type === "SORTIE" ||
          type === "AJUSTEMENT" ||
          type === "PERDU")
      ) {
        // Recharger les produits pour avoir les quantités à jour
        await get().fetchProducts();
      }

      set({ loading: false });
      return newMovement;
    } catch (error) {
      set({
        error:
          error.response?.data?.message ||
          "Erreur lors de la création du mouvement",
        loading: false,
      });
      throw error;
    }
  },

  // Récupérer tous les mouvements
  fetchAllMovements: async () => {
    set({ movementsLoading: true, error: null });
    try {
      const response = await axiosToken.get("/products/movements/all");
      set({
        movements: response.data.data?.movements || response.data.data || [],
        movementsLoading: false,
      });
    } catch (error) {
      set({
        error:
          error.response?.data?.message ||
          "Erreur lors de la récupération des mouvements",
        movementsLoading: false,
      });
      throw error;
    }
  },

  // Récupérer un mouvement par ID
  fetchMovementById: async (id) => {
    set({ movementsLoading: true, error: null });
    try {
      const response = await axiosToken.get(`/products/movements/id/${id}`);
      set({
        currentMovement: response.data.data?.movement || response.data.data,
        movementsLoading: false,
      });
      return response.data.data?.movement || response.data.data;
    } catch (error) {
      set({
        error:
          error.response?.data?.message ||
          "Erreur lors de la récupération du mouvement",
        movementsLoading: false,
      });
      throw error;
    }
  },

  // =============================================
  // RAPPORTS ET STATISTIQUES
  // =============================================

  // Rapport d'inventaire - MODIFIÉ
  fetchInventoryReport: async () => {
    set({ loading: true, error: null });
    try {
      const response = await axiosToken.get("/products/reports/inventory");
      const reportData = response.data.data;

      // Transformer les données pour correspondre à l'interface attendue
      const transformedReport =
        reportData.produits?.map((item) => ({
          ...item,
          id: item.product.id,
          libelle: item.product.libelle,
          description: item.product.description,
          quantity_initiale: item.totalEntrees - item.totalSorties,
          entrees: item.totalEntrees,
          sorties: item.totalSorties,
          stock_final: item.stockActuel,
          variation: 0,
          quantity: item.stockActuel,
          seuilAlerte: item.seuilAlerte,
        })) || [];

      set({
        inventoryReport: transformedReport,
        loading: false,
      });
    } catch (error) {
      set({
        error:
          error.response?.data?.message ||
          "Erreur lors de la récupération du rapport d'inventaire",
        loading: false,
      });
      throw error;
    }
  },

  // Historique des mouvements d'un produit
  fetchProductHistory: async (productId) => {
    set({ loading: true, error: null });
    try {
      const response = await axiosToken.get(
        `/products/reports/history/${productId}`,
      );
      // console.log(
      //   "response.data.data?.movements: ",
      //   response.data.data?.movements
      // );
      set({
        historyReport:
          response.data.data?.movements || response.data.data || [],
        loading: false,
      });
    } catch (error) {
      set({
        error:
          error.response?.data?.message ||
          "Erreur lors de la récupération de l'historique",
        loading: false,
      });
      throw error;
    }
  },

  // Statistiques générales
  fetchStatisticsReport: async () => {
    set({ loading: true, error: null });
    try {
      const response = await axiosToken.get("/products/reports/statistics");
      set({
        statisticsReport: response.data.data || {},
        loading: false,
      });
    } catch (error) {
      set({
        error:
          error.response?.data?.message ||
          "Erreur lors de la récupération des statistiques",
        loading: false,
      });
      throw error;
    }
  },

  // Export PDF du rapport d'inventaire
  exportInventoryPDF: async () => {
    set({ loading: true, error: null });
    try {
      // 1. Récupérer les données du rapport
      const response = await axiosToken.get("/products/reports/inventory");
      const reportData = response.data.data;

      // 2. Vérifier que nous avons des données
      if (!reportData.produits || reportData.produits.length === 0) {
        throw new Error("Aucune donnée disponible pour générer le rapport");
      }

      // 3. Importer jsPDF dynamiquement
      const { jsPDF } = await import("jspdf");

      // 4. Créer le document PDF en format paysage
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      // 5. Configuration
      const margin = 10;
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const contentWidth = pageWidth - 2 * margin;

      // 6. Largeurs fixes et espacées des colonnes
      const columnWidths = {
        code: 40, // Largeur fixe pour le code
        libelle: 40, // Largeur fixe pour le libellé
        description: 45, // Largeur fixe pour la description
        stock: 18, // Largeur fixe pour le stock
        seuil: 18, // Largeur fixe pour le seuil
        entrees: 20, // Largeur fixe pour les entrées
        sorties: 20, // Largeur fixe pour les sorties
        statut: 25, // Largeur fixe pour le statut
      };

      // 7. Calcul des positions X avec espacement
      const columns = {
        code: margin,
        libelle: margin + columnWidths.code + 4, // +2mm d'espace
        description: margin + columnWidths.code + columnWidths.libelle + 6,
        stock:
          margin +
          columnWidths.code +
          columnWidths.libelle +
          columnWidths.description +
          8,
        seuil:
          margin +
          columnWidths.code +
          columnWidths.libelle +
          columnWidths.description +
          columnWidths.stock +
          10,
        entrees:
          margin +
          columnWidths.code +
          columnWidths.libelle +
          columnWidths.description +
          columnWidths.stock +
          columnWidths.seuil +
          12,
        sorties:
          margin +
          columnWidths.code +
          columnWidths.libelle +
          columnWidths.description +
          columnWidths.stock +
          columnWidths.seuil +
          columnWidths.entrees +
          14,
        statut:
          margin +
          columnWidths.code +
          columnWidths.libelle +
          columnWidths.description +
          columnWidths.stock +
          columnWidths.seuil +
          columnWidths.entrees +
          columnWidths.sorties +
          16,
      };

      // 8. Fonction pour diviser le texte
      const splitText = (text, maxWidth, fontSize = 9) => {
        if (!text) return [""];
        const words = text.toString().split(" ");
        const lines = [];
        let currentLine = "";

        doc.setFontSize(fontSize);

        for (let i = 0; i < words.length; i++) {
          const word = words[i];
          const testLine = currentLine ? currentLine + " " + word : word;
          const testWidth = doc.getTextWidth(testLine);

          if (testWidth > maxWidth && currentLine !== "") {
            lines.push(currentLine);
            currentLine = word;
          } else {
            currentLine = testLine;
          }
        }
        lines.push(currentLine);
        return lines;
      };

      // 9. Fonction pour dessiner une cellule
      const drawCell = (
        text,
        x,
        y,
        width,
        height,
        fontSize = 9,
        align = "left",
        maxLines = 2,
      ) => {
        doc.setFontSize(fontSize);
        const lines = splitText(text, width - 4, fontSize);

        const displayLines = lines.slice(0, maxLines);
        if (lines.length > maxLines) {
          displayLines[maxLines - 1] =
            displayLines[maxLines - 1].substring(
              0,
              displayLines[maxLines - 1].length - 3,
            ) + "...";
        }

        let lineY = y + 4;
        displayLines.forEach((line) => {
          if (align === "center") {
            doc.text(line, x + width / 2, lineY, { align: "center" });
          } else if (align === "right") {
            doc.text(line, x + width - 2, lineY, { align: "right" });
          } else {
            doc.text(line, x + 2, lineY);
          }
          lineY += 3.5;
        });
      };

      // 10. En-tête du document
      doc.setFontSize(16);
      doc.setTextColor(40, 40, 40);
      doc.text("RAPPORT D'INVENTAIRE", margin, 15);

      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text(
        `Généré le: ${new Date().toLocaleDateString("fr-FR")}`,
        margin,
        22,
      );

      // 11. En-tête du tableau
      const headerY = 30;
      const rowHeight = 10;

      // Dessiner l'en-tête avec fond bleu
      doc.setFillColor(59, 130, 246);
      doc.rect(margin, headerY, contentWidth, rowHeight, "F");

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.setFont(undefined, "bold");

      // En-têtes des colonnes - BIEN ESPACÉS
      doc.text("CODE", columns.code + columnWidths.code / 2, headerY + 6, {
        align: "center",
      });
      doc.text(
        "PRODUIT",
        columns.libelle + columnWidths.libelle / 2,
        headerY + 6,
        { align: "center" },
      );
      doc.text(
        "DESCRIPTION",
        columns.description + columnWidths.description / 2,
        headerY + 6,
        { align: "center" },
      );
      doc.text("STOCK", columns.stock + columnWidths.stock / 2, headerY + 6, {
        align: "center",
      });
      doc.text("SEUIL", columns.seuil + columnWidths.seuil / 2, headerY + 6, {
        align: "center",
      });
      doc.text(
        "ENTRÉES",
        columns.entrees + columnWidths.entrees / 2,
        headerY + 6,
        { align: "center" },
      );
      doc.text(
        "SORTIES",
        columns.sorties + columnWidths.sorties / 2,
        headerY + 6,
        { align: "center" },
      );
      doc.text(
        "STATUT",
        columns.statut + columnWidths.statut / 2,
        headerY + 6,
        { align: "center" },
      );

      // 12. Données du tableau
      let currentY = headerY + rowHeight;
      doc.setFont(undefined, "normal");

      reportData.produits.forEach((item, index) => {
        // Vérifier si besoin d'une nouvelle page
        if (currentY + rowHeight > pageHeight - margin) {
          doc.addPage();
          currentY = margin;

          // Redessiner l'en-tête sur la nouvelle page
          doc.setFillColor(59, 130, 246);
          doc.rect(margin, currentY, contentWidth, rowHeight, "F");
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(8);
          doc.setFont(undefined, "bold");

          doc.text("CODE", columns.code + columnWidths.code / 2, currentY + 6, {
            align: "center",
          });
          doc.text(
            "PRODUIT",
            columns.libelle + columnWidths.libelle / 2,
            currentY + 6,
            { align: "center" },
          );
          doc.text(
            "DESCRIPTION",
            columns.description + columnWidths.description / 2,
            currentY + 6,
            { align: "center" },
          );
          doc.text(
            "STOCK",
            columns.stock + columnWidths.stock / 2,
            currentY + 6,
            { align: "center" },
          );
          doc.text(
            "SEUIL",
            columns.seuil + columnWidths.seuil / 2,
            currentY + 6,
            { align: "center" },
          );
          doc.text(
            "ENTRÉES",
            columns.entrees + columnWidths.entrees / 2,
            currentY + 6,
            { align: "center" },
          );
          doc.text(
            "SORTIES",
            columns.sorties + columnWidths.sorties / 2,
            currentY + 6,
            { align: "center" },
          );
          doc.text(
            "STATUT",
            columns.statut + columnWidths.statut / 2,
            currentY + 6,
            { align: "center" },
          );

          currentY += rowHeight;
        }

        // Alternance de couleurs pour les lignes
        if (index % 2 === 0) {
          doc.setFillColor(248, 250, 252);
          doc.rect(margin, currentY, contentWidth, rowHeight, "F");
        }

        // Couleur par défaut
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(8);

        // Code produit
        drawCell(
          item.product.code,
          columns.code,
          currentY,
          columnWidths.code,
          rowHeight,
          8,
          "center",
          1,
        );

        // Libellé du produit
        drawCell(
          item.product.libelle,
          columns.libelle,
          currentY,
          columnWidths.libelle,
          rowHeight,
          8,
          "left",
          2,
        );

        // Description
        drawCell(
          item.product.description,
          columns.description,
          currentY,
          columnWidths.description,
          rowHeight,
          8,
          "left",
          2,
        );

        // Données numériques (centrées)
        doc.text(
          item.stockActuel.toString(),
          columns.stock + columnWidths.stock / 2,
          currentY + 6,
          { align: "center" },
        );
        doc.text(
          item.seuilAlerte.toString(),
          columns.seuil + columnWidths.seuil / 2,
          currentY + 6,
          { align: "center" },
        );
        doc.text(
          item.totalEntrees.toString(),
          columns.entrees + columnWidths.entrees / 2,
          currentY + 6,
          { align: "center" },
        );
        doc.text(
          item.totalSorties.toString(),
          columns.sorties + columnWidths.sorties / 2,
          currentY + 6,
          { align: "center" },
        );

        // Statut avec couleur
        if (item.statutAlerte === "CRITIQUE") {
          doc.setTextColor(220, 38, 38);
        } else if (item.statutAlerte === "ALERTE") {
          doc.setTextColor(245, 158, 11);
        } else {
          doc.setTextColor(34, 197, 94);
        }
        doc.text(
          item.statutAlerte,
          columns.statut + columnWidths.statut / 2,
          currentY + 6,
          { align: "center" },
        );

        currentY += rowHeight;
      });

      // 13. Légende des statuts
      // const legendY = pageHeight - 15;
      // doc.setFontSize(7);
      // doc.setTextColor(100, 100, 100);
      // doc.text("Légende: ", margin, legendY);

      // doc.setTextColor(34, 197, 94);
      // doc.text("● NORMAL", margin + 20, legendY);

      // doc.setTextColor(245, 158, 11);
      // doc.text("● ALERTE", margin + 45, legendY);

      // doc.setTextColor(220, 38, 38);
      // doc.text("● CRITIQUE", margin + 70, legendY);

      // 14. Pied de page
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(7);
        doc.setTextColor(100, 100, 100);
        doc.text(
          `Page ${i}/${pageCount}`,
          pageWidth - margin,
          pageHeight - 10,
          { align: "right" },
        );
      }

      // 15. Télécharger le PDF
      doc.save(
        `rapport-inventaire-${new Date().toISOString().split("T")[0]}.pdf`,
      );

      set({ loading: false });
      return true;
    } catch (error) {
      console.error("Erreur détaillée:", error);
      set({
        error: error.message || "Erreur lors de la génération du PDF",
        loading: false,
      });
      throw error;
    }
  },

  // =============================================
  // GESTION DES ALERTES
  // =============================================

  // Récupérer toutes les alertes
  fetchAllAlerts: async () => {
    set({ alertsLoading: true, error: null });
    try {
      const response = await axiosToken.get("/products/alerts/all");
      set({
        alerts: response.data.data?.alerts || response.data.data || [],
        alertsLoading: false,
      });
    } catch (error) {
      set({
        error:
          error.response?.data?.message ||
          "Erreur lors de la récupération des alertes",
        alertsLoading: false,
      });
      throw error;
    }
  },

  // Marquer une alerte comme résolue
  resolveAlert: async (alertId) => {
    set({ alertsLoading: true, error: null });
    try {
      const response = await axiosToken.patch(
        `/products/alerts/${alertId}/resolve`,
      );
      const updatedAlert = response.data.data?.alert || response.data.data;

      // Mettre à jour dans la liste
      set((state) => ({
        alerts: state.alerts.map((alert) =>
          alert.id === parseInt(alertId) ? updatedAlert : alert,
        ),
        alertsLoading: false,
      }));

      return updatedAlert;
    } catch (error) {
      set({
        error:
          error.response?.data?.message ||
          "Erreur lors de la résolution de l'alerte",
        alertsLoading: false,
      });
      throw error;
    }
  },

  // Vérifier toutes les alertes
  checkAllAlerts: async () => {
    set({ alertsLoading: true, error: null });
    try {
      const response = await axiosToken.post("/products/alerts/check-all");
      set({
        alerts: response.data.data?.alerts || response.data.data || [],
        alertsLoading: false,
      });
    } catch (error) {
      set({
        error:
          error.response?.data?.message ||
          "Erreur lors de la vérification des alertes",
        alertsLoading: false,
      });
      throw error;
    }
  },

  // Supprimer une alerte
  deleteAlert: async (alertId) => {
    set({ alertsLoading: true, error: null });
    try {
      await axiosToken.delete(`/products/alerts/${alertId}`);

      // Retirer de la liste
      set((state) => ({
        alerts: state.alerts.filter((alert) => alert.id !== parseInt(alertId)),
        alertsLoading: false,
      }));
    } catch (error) {
      set({
        error:
          error.response?.data?.message ||
          "Erreur lors de la suppression de l'alerte",
        alertsLoading: false,
      });
      throw error;
    }
  },

  // =============================================
  // UTILITAIRES
  // =============================================

  // Réinitialiser les erreurs
  clearError: () => set({ error: null }),

  // Réinitialiser le produit courant
  clearCurrentProduct: () => set({ currentProduct: null }),

  // Réinitialiser le mouvement courant
  clearCurrentMovement: () => set({ currentMovement: null }),

  // Réinitialiser les rapports
  clearReports: () =>
    set({
      inventoryReport: [],
      statisticsReport: null,
      historyReport: [],
    }),

  // Réinitialiser les alertes
  clearAlerts: () => set({ alerts: [] }),

  // Obtenir un produit par ID depuis le store (sans appel API)
  getProductById: (id) => {
    return get().products.find((p) => p.id === parseInt(id));
  },

  // Obtenir les produits en stock bas
  getLowStockProducts: () => {
    const products = get().products;
    return products.filter((p) => {
      const quantity = p.quantity || p.stockActuel || 0;
      const seuilAlerte = p.seuilAlerte || 0;
      return quantity <= seuilAlerte;
    });
  },

  // Obtenir les produits actifs
  getActiveProducts: () => {
    return get().products.filter((p) => p.isActive);
  },
}));
